import { useState } from "react";
import { api, apiErr, API } from "@/lib/api";
import { useFetch, Field } from "@/lib/ui";
import { useLang } from "@/context/LangContext";
import { Empty } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GRADES } from "@/lib/grades";
import { Plus, Trash2, Upload, Eye, CheckCircle2, Circle, Award } from "lucide-react";

export default function ModulesPanel({ canManage = false }) {
  const { L } = useLang();
  const { data: modules, loading, reload } = useFetch(() => api.get("/lms/modules"));
  const { data: progress, reload: reloadProg } = useFetch(() => api.get("/lms/progress"));
  const [filter, setFilter] = useState("all");
  const [nf, setNf] = useState({ grade_level: "", week: "", course: "", title: "" });
  const [viewing, setViewing] = useState(null);

  const add = async () => {
    if (!nf.grade_level || !nf.title.trim()) { toast.error(L("Grade and title are required", "Niveau et titre requis")); return; }
    try {
      await api.post("/lms/modules", { grade_level: nf.grade_level, week: nf.week === "" ? null : Number(nf.week), course: nf.course || null, title: nf.title.trim() });
      toast.success(L("Module added", "Module ajouté")); setNf({ grade_level: "", week: "", course: "", title: "" }); reload(); reloadProg();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggle = async (m) => {
    try { const r = await api.post(`/lms/modules/${m.id}/complete`, {}); toast.success(r.completed ? L("Marked completed", "Marqué terminé") : L("Marked not done", "Marqué non fait")); reload(); reloadProg(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(L(`Are you sure you want to delete ${m.title}?`, `Êtes-vous sûr de vouloir supprimer ${m.title} ?`))) return;
    try { await api.del(`/lms/modules/${m.id}`); reload(); reloadProg(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const uploadGuide = async (m, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.raw.post(`/lms/modules/${m.id}/guide`, fd); toast.success(L("Guide uploaded", "Guide téléversé")); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const dlCertificate = async (m) => {
    try {
      const r = await api.raw.get(`/lms/modules/${m.id}/certificate`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = `certificat_${(m.grade_level || "cours").replace(/\s/g, "_")}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch (e) {
      if (e.response?.status === 400) toast.error(L("Mark the course completed first", "Marquez d'abord le cours comme terminé"));
      else toast.error(apiErr(e.response?.data?.detail) || L("Download failed", "Échec du téléchargement"));
    }
  };

  if (loading) return <Empty title={L("Loading…", "Chargement…")} />;
  const mods = (modules || []).filter((m) => filter === "all" || m.grade_level === filter);
  const byGrade = {};
  mods.forEach((m) => { (byGrade[m.grade_level] = byGrade[m.grade_level] || []).push(m); });

  return (
    <div className="space-y-6">
      {/* Progress overview */}
      <div>
        <p className="font-heading font-bold mb-2">{L("Curriculum progress", "Progression du curriculum")}</p>
        {(progress || []).length === 0 ? <p className="text-sm text-muted-foreground">{L("No modules yet.", "Aucun module.")}</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lms-progress">
            {progress.map((p) => (
              <div key={p.grade_level} className="bg-card border border-border rounded-lg p-3" data-testid={`progress-${p.grade_level}`}>
                <p className="text-sm font-medium">{p.grade_level}</p>
                <div className="h-2 bg-secondary rounded mt-2 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${p.percent}%` }} /></div>
                <p className="text-xs text-muted-foreground mt-1">{p.completed}/{p.total} · {p.percent}%</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {canManage && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="module-add-card">
          <p className="font-heading font-bold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" />{L("Add a module", "Ajouter un module")}</p>
          <div className="grid sm:grid-cols-4 gap-3">
            <Field label={L("Grade", "Niveau")}>
              <Select value={nf.grade_level} onValueChange={(v) => setNf({ ...nf, grade_level: v })}>
                <SelectTrigger data-testid="module-grade-select"><SelectValue placeholder={L("Select", "Choisir")} /></SelectTrigger>
                <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={L("Week", "Semaine")}><Input type="number" value={nf.week} onChange={(e) => setNf({ ...nf, week: e.target.value })} data-testid="module-week-input" /></Field>
            <Field label={L("Course", "Cours")}><Input value={nf.course} onChange={(e) => setNf({ ...nf, course: e.target.value })} data-testid="module-course-input" /></Field>
            <Field label={L("Module title", "Titre du module")}><Input value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} data-testid="module-title-input" /></Field>
          </div>
          <Button className="mt-3" onClick={add} data-testid="module-add-btn">{L("Add module", "Ajouter")}</Button>
        </div>
      )}

      <div className="max-w-xs">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="module-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{L("All grades", "Tous les niveaux")}</SelectItem>
            {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {mods.length === 0 ? <Empty title={L("No modules", "Aucun module")} /> : Object.keys(byGrade).map((g) => (
        <div key={g} data-testid={`modules-grade-${g}`}>
          <p className="font-heading font-bold text-lg mb-2">{g}</p>
          <div className="space-y-2">
            {byGrade[g].map((m) => (
              <div key={m.id} className="bg-card border border-border rounded-lg" data-testid={`module-${m.id}`}>
                <div className="p-4 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggle(m)} data-testid={`module-toggle-${m.id}`} className="shrink-0" title={L("Lesson completed", "Leçon terminée")}>
                      {m.completed ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> : <Circle className="h-6 w-6 text-muted-foreground" />}
                    </button>
                    <div>
                      <p className="font-medium">{m.title}</p>
                      <p className="text-xs text-muted-foreground">{m.week != null ? `${L("Week", "Semaine")} ${m.week}` : ""}{m.course ? ` · ${m.course}` : ""}{m.completed && m.completed_by ? ` · ${L("done by", "fait par")} ${m.completed_by}` : ""}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.completed ? <Badge className="bg-emerald-600 hover:bg-emerald-600">{L("Completed", "Terminé")}</Badge> : <Badge variant="secondary">{L("In progress", "En cours")}</Badge>}
                    {m.completed && <Button size="sm" variant="outline" onClick={() => dlCertificate(m)} data-testid={`module-cert-${m.id}`}><Award className="h-4 w-4 mr-1" />{L("Certificate", "Certificat")}</Button>}
                    {m.guide && <Button size="sm" variant="outline" onClick={() => setViewing(viewing === m.id ? null : m.id)} data-testid={`module-guide-view-${m.id}`}><Eye className="h-4 w-4 mr-1" />{L("Guide", "Guide")}</Button>}
                    {canManage && (
                      <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer">
                        <Upload className="h-3.5 w-3.5" />{m.guide ? L("Replace guide", "Remplacer") : L("Add guide", "Ajouter guide")}
                        <input type="file" className="hidden" accept=".pdf" onChange={(e) => uploadGuide(m, e.target.files?.[0])} data-testid={`module-guide-upload-${m.id}`} />
                      </label>
                    )}
                    {canManage && <Button size="icon" variant="ghost" onClick={() => del(m)} data-testid={`module-del-${m.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </div>
                </div>
                {viewing === m.id && m.guide && (
                  <div className="border-t border-border p-3">
                    <p className="text-xs text-muted-foreground mb-2">{L("Facilitator Guide — private to staff.", "Guide du facilitateur — privé au personnel.")}</p>
                    <div className="relative w-full" style={{ height: "70vh" }}>
                      <iframe title={m.title} src={`${API}/lms/modules/${m.id}/guide/view#toolbar=0&navpanes=0`} className="w-full h-full border border-border rounded" data-testid={`module-guide-frame-${m.id}`} />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
